import { Button } from "@msuite/ui/button";
import { useLoading } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import { Tooltip } from "@msuite/ui/tooltip";
import { downloadFile, responseHandler } from "@msuite/ui/utils";
import { client } from "@/hono";

/** Props Interface */
interface PaymentFileDownloadButtonProps {
	paymentRunId: string;
	size?: "xxs" | "xs";
	/** Why there is no file to download — a run settled entirely by offset has none. */
	disabledReason?: string | null;
}

/**
 * Download a payment file again, exactly as it was handed out. The document is fetched on the
 * press, so a list of files carries none of them. A run whose payments were all settled by offset
 * has no document; the button is disabled with the reason when the caller knows, and the press
 * says so when it does not.
 */
export function PaymentFileDownloadButton({
	paymentRunId,
	size = "xs",
	disabledReason = null,
}: PaymentFileDownloadButtonProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const loader = useLoading();

	/** Functions */
	async function handleOnDownload() {
		try {
			loader.start();
			const response = await client.incoming_invoices["payment-runs"][":paymentRunId"].$get({
				param: { paymentRunId },
			});
			const { paymentRun } = await responseHandler(response);
			if (!paymentRun.document) {
				toast.info(t("incoming_invoices.payments.offsets.no_file"));
				return;
			}
			downloadFile(
				new Blob([paymentRun.document], { type: "application/xml" }),
				paymentRun.fileName,
			);
		} finally {
			loader.stop();
		}
	}

	/** Render */
	return (
		<Tooltip label={disabledReason ?? t("incoming_invoices.payments.download_file")}>
			<Button
				size={size}
				variant="ghost"
				icon={<Icon name="Download" />}
				onClick={handleOnDownload}
				isLoading={loader.isLoading}
				isDisabled={!!disabledReason}
				data-cy="download-incoming-invoice-payment-file-button"
			/>
		</Tooltip>
	);
}
